import { useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiError } from "../api";
import { useAuth } from "../context/AuthContext";
import { StatusBadge } from "../components/StatusBadge";
import PauseReasonDialog from "../components/PauseReasonDialog";
import { toast } from "sonner";
import { Play, Pause, Square, CheckCircle2, RotateCcw, Bell, BellOff, BellRing, PhoneCall, Clock, SkipForward } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  fireNaVezNotification,
  notificationsSupported,
  notificationPermission,
  requestNotificationPermission,
  playBeep,
} from "../lib/notify";

const REASONS = [
  { key: "banheiro", label: "Banheiro", auto_pause: false, emoji: "🚻" },
  { key: "almoco", label: "Almoço", auto_pause: true, emoji: "🍽️" },
  { key: "procedimento", label: "Procedimento particular", auto_pause: false, emoji: "💅" },
  { key: "outro", label: "Outro", auto_pause: false, emoji: "✏️" },
];

function CallCountdown({ timeoutAt, serverTime, onExpire }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, []);
  const target = new Date(timeoutAt).getTime();
  const anchor = serverTime ? new Date(serverTime).getTime() : Date.now();
  const drift = anchor - Date.now();
  const remaining = Math.max(0, Math.floor((target - now - drift) / 1000));
  useEffect(() => {
    if (remaining === 0 && onExpire) onExpire();
  }, [remaining, onExpire]);
  return (
    <span data-testid="incoming-call-countdown" className="inline-flex items-center gap-1 rounded-full bg-white/20 px-3 py-1 text-xs font-semibold text-white">
      <Clock size={12} /> {remaining}s
    </span>
  );
}

export default function ProfessionalHome() {
  const { user, refresh } = useAuth();
  const [state, setState] = useState({ categories: [], professionals: [], my_active_call: null, server_time: null });
  const wasNextRef = useRef(false);
  const activeCallIdRef = useRef(null);
  const beepIntervalRef = useRef(null);
  const [permission, setPermission] = useState(notificationsSupported() ? notificationPermission() : "unsupported");
  const [reasonDialog, setReasonDialog] = useState(null); // { call } or { category, self: true }
  const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
  const [freeNote, setFreeNote] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/queue");
      setState(data);
      await refresh();
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.debug("[minha-vez] poll error", err);
    }
  }, [refresh]);

  useEffect(() => {
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [load]);

  const me = user ? (state.professionals.find((p) => p.id === user.id) || user) : null;
  const myCats = me ? state.categories.filter((c) => (me.category_ids || []).includes(c.id)) : [];
  const nextCats = me ? myCats.filter((c) => c.na_vez_id === me.id) : [];
  const isNextInAny = nextCats.length > 0;
  const myStatus = me?.status;
  const activeCall = state.my_active_call || null;

  // Notification "na vez"
  useEffect(() => {
    if (!me) return;
    const isNextNow = isNextInAny && myStatus === "available" && !activeCall;
    if (isNextNow && !wasNextRef.current) {
      fireNaVezNotification(nextCats.map((c) => c.name));
      toast.success("Você é a próxima da vez!", { duration: 6000 });
    }
    wasNextRef.current = isNextNow;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNextInAny, myStatus, activeCall]);

  // Sound loop while a call is pending
  useEffect(() => {
    if (activeCall && activeCall.id !== activeCallIdRef.current) {
      activeCallIdRef.current = activeCall.id;
      // fire immediate strong alert
      const cat = state.categories.find((c) => c.id === activeCall.category_id);
      fireNaVezNotification(cat ? [`Chamada: ${cat.name}`] : ["Chamada da recepção"]);
      if (beepIntervalRef.current) clearInterval(beepIntervalRef.current);
      beepIntervalRef.current = setInterval(() => playBeep(), 6000);
    }
    if (!activeCall && beepIntervalRef.current) {
      clearInterval(beepIntervalRef.current);
      beepIntervalRef.current = null;
      activeCallIdRef.current = null;
    }
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCall?.id]);

  useEffect(() => {
    return () => {
      if (beepIntervalRef.current) clearInterval(beepIntervalRef.current);
    };
  }, []);

  if (!user || !me) return null;

  const currentAttendance = me.current_attendance_id;
  const currentCategory = myCats.find((c) => c.id === me.current_category_id);
  const canStart = me.status === "off_duty";
  const canPause = me.status === "available";
  const canResume = me.status === "paused";
  const canEnd = me.status !== "off_duty" && me.status !== "serving";

  const doAction = async (path, msg) => {
    try {
      await api.post(path);
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const submitPause = async ({ reason, note }) => {
    try {
      const { data } = await api.post(`/professionals/${user.id}/pause`, { reason, note });
      toast.success(data?.left_queues ? "Bom almoço! Você saiu das filas." : "Você está em pausa");
      setPauseDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const finish = async () => {
    if (!currentAttendance) return;
    try {
      await api.post("/queue/finish", { attendance_id: currentAttendance });
      toast.success("Atendimento finalizado");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const acceptCall = async () => {
    if (!activeCall) return;
    try {
      await api.post(`/calls/${activeCall.id}/accept`);
      toast.success("Atendimento iniciado");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openDeclineDialog = () => {
    setFreeNote("");
    setReasonDialog({ call: activeCall });
  };

  const openSelfPassDialog = (category) => {
    setFreeNote("");
    setReasonDialog({ category, self: true });
  };

  const submitReason = async (reasonKey) => {
    const reason = REASONS.find((r) => r.key === reasonKey);
    if (!reason) return;
    try {
      if (reasonDialog?.call) {
        await api.post(`/calls/${reasonDialog.call.id}/decline`, {
          reason: reasonKey,
          note: freeNote || null,
          also_pause: reason.auto_pause,
        });
      } else if (reasonDialog?.self && reasonDialog?.category) {
        await api.post("/queue/pass-turn", {
          category_id: reasonDialog.category.id,
          professional_id: user.id,
          reason: reasonKey,
          note: freeNote || null,
          also_pause: reason.auto_pause,
        });
      }
      toast.success(reason.auto_pause ? "Você passou a vez e ficou em pausa" : "Você passou a vez");
      setReasonDialog(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const askPermission = async () => {
    const p = await requestNotificationPermission();
    setPermission(p);
    if (p === "granted") {
      playBeep();
      toast.success("Notificações ativadas");
    } else if (p === "denied") {
      toast.error("Permissão negada. Ative nas configurações do navegador.");
    }
  };

  const callCategory = activeCall ? state.categories.find((c) => c.id === activeCall.category_id) : null;

  return (
    <div className="max-w-2xl">
      {/* Incoming Call Modal (blocking overlay) */}
      {activeCall && (
        <div
          data-testid="incoming-call-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
        >
          <div className="w-full max-w-md rounded-3xl bg-black p-6 sm:p-8 text-white shadow-2xl na-vez-badge">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] opacity-70">
                <PhoneCall size={14} /> Chamada
              </div>
              <CallCountdown timeoutAt={activeCall.timeout_at} serverTime={state.server_time} onExpire={load} />
            </div>
            <div className="mt-4">
              <div className="text-[11px] uppercase tracking-widest opacity-70">A recepção está chamando</div>
              <div className="font-display text-3xl font-semibold tracking-tight">
                {callCategory?.name || "Atendimento"}
              </div>
              <div className="mt-2 text-sm opacity-80">Toque em Aceitar para iniciar o atendimento agora.</div>
            </div>
            <div className="mt-6 grid grid-cols-1 gap-3">
              <button
                data-testid="btn-accept-call"
                onClick={acceptCall}
                className="min-h-[56px] rounded-full bg-white text-black px-6 text-base font-semibold uppercase tracking-widest hover:-translate-y-[1px] transition-transform"
              >
                Aceitar e atender
              </button>
              <button
                data-testid="btn-decline-call"
                onClick={openDeclineDialog}
                className="min-h-[56px] rounded-full border border-white/30 bg-white/5 px-6 text-base font-semibold uppercase tracking-widest text-white hover:bg-white/10"
              >
                Passar a vez
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.25em] text-gray-500">Olá,</div>
        <h1 className="font-display text-3xl sm:text-4xl font-medium tracking-tight">{me.name}</h1>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <StatusBadge status={me.status} pause_reason={me.pause_reason} size="lg" testid="my-status-badge" />
          {permission === "default" && (
            <button
              data-testid="btn-enable-notifications"
              onClick={askPermission}
              className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-pink-50"
            >
              <BellRing size={12} /> Ativar avisos "Na vez"
            </button>
          )}
          {permission === "granted" && (
            <span
              data-testid="notifications-enabled"
              className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700"
            >
              <BellRing size={12} /> Avisos ativados
            </span>
          )}
          {permission === "denied" && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-700">
              <BellOff size={12} /> Avisos bloqueados no navegador
            </span>
          )}
        </div>
      </div>

      {isNextInAny && me.status === "available" && !activeCall && (
        <div data-testid="na-vez-alert" className="mb-6 rounded-2xl border-2 border-black bg-black p-5 text-white na-vez-badge">
          <div className="flex items-center gap-3">
            <Bell size={22} />
            <div>
              <div className="text-[11px] uppercase tracking-[0.3em] opacity-70">Atenção</div>
              <div className="font-display text-xl font-semibold">Você é a próxima da vez</div>
            </div>
          </div>
          <div className="mt-2 text-sm opacity-80">
            {nextCats.map((c) => c.name).join(" · ")}
          </div>
        </div>
      )}

      {me.status === "serving" && (
        <div data-testid="serving-panel" className="mb-6 rounded-2xl border border-red-200 bg-red-50 p-5">
          <div className="text-[11px] uppercase tracking-[0.25em] text-red-600">Em atendimento</div>
          <div className="font-display text-2xl font-semibold text-red-700">
            {currentCategory?.name || me.current_category_id || "—"}
          </div>
          <div className="mt-1 text-xs text-red-600/80">
            Tipo: {me.current_attendance_type === "vez" ? "VEZ" : "PREFERÊNCIA"}
          </div>
          <button
            data-testid="btn-finish-attendance"
            onClick={finish}
            className="mt-4 w-full min-h-[48px] rounded-full bg-red-500 px-6 py-3 text-sm font-semibold uppercase tracking-widest text-white hover:bg-red-600"
          >
            Finalizar atendimento
          </button>
        </div>
      )}

      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {canStart && (
          <button
            data-testid="btn-start-shift"
            onClick={() => doAction(`/professionals/${user.id}/start-shift`, "Expediente iniciado")}
            className="min-h-[48px] rounded-full bg-black px-6 py-3 text-sm font-semibold uppercase tracking-widest text-white hover:-translate-y-[1px] transition-transform inline-flex items-center justify-center gap-2"
          >
            <Play size={16} /> Iniciar expediente
          </button>
        )}
        {canPause && (
          <button
            data-testid="btn-pause"
            onClick={() => setPauseDialogOpen(true)}
            className="min-h-[48px] rounded-full border border-gray-200 bg-white px-6 py-3 text-sm font-semibold uppercase tracking-widest text-gray-800 hover:bg-yellow-50 inline-flex items-center justify-center gap-2"
          >
            <Pause size={16} /> Pausar
          </button>
        )}
        {canResume && (
          <button
            data-testid="btn-resume"
            onClick={() => doAction(`/professionals/${user.id}/resume`, "Você voltou para a fila")}
            className="min-h-[48px] rounded-full bg-black px-6 py-3 text-sm font-semibold uppercase tracking-widest text-white inline-flex items-center justify-center gap-2"
          >
            <RotateCcw size={16} /> Voltar à disponibilidade
          </button>
        )}
        {canEnd && (
          <button
            data-testid="btn-end-shift"
            onClick={() => {
              if (window.confirm("Tem certeza que deseja encerrar seu expediente?"))
                doAction(`/professionals/${user.id}/end-shift`, "Expediente encerrado");
            }}
            className="min-h-[48px] rounded-full border border-gray-200 bg-white px-6 py-3 text-sm font-semibold uppercase tracking-widest text-gray-800 hover:bg-gray-50 inline-flex items-center justify-center gap-2"
          >
            <Square size={16} /> Encerrar expediente
          </button>
        )}
      </div>

      <h2 className="mb-3 font-display text-xl font-semibold tracking-tight">Minhas filas</h2>
      {myCats.length === 0 && (
        <div className="rounded-2xl border border-dashed border-gray-200 p-6 text-center text-sm text-gray-400">
          Você ainda não está em nenhuma categoria. Peça à recepção para atribuir.
        </div>
      )}
      <div className="space-y-3">
        {myCats.map((c) => {
          const myIdx = c.queue.findIndex((p) => p.id === user.id);
          const inQueue = myIdx >= 0;
          const before = inQueue && myIdx > 0 ? c.queue[myIdx - 1] : null;
          const after = inQueue && myIdx < c.queue.length - 1 ? c.queue[myIdx + 1] : null;
          const isNext = c.na_vez_id === user.id;
          return (
            <div key={c.id} data-testid={`my-cat-${c.name.toLowerCase()}`} className={`rounded-2xl border p-5 ${isNext ? "border-black bg-pink-50/60" : "border-gray-200 bg-white"}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-gray-500">Categoria</div>
                  <div className="font-display text-xl font-semibold tracking-tight">{c.name}</div>
                </div>
                {inQueue ? (
                  <div className="text-right">
                    <div className="text-[11px] uppercase tracking-widest text-gray-500">Posição</div>
                    <div className="font-display text-2xl font-semibold">
                      {myIdx + 1}ª
                    </div>
                  </div>
                ) : (
                  <span className="text-xs text-gray-400">Fora da fila</span>
                )}
              </div>
              {inQueue && (
                <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-gray-500">Antes de você</div>
                    <div className="font-medium">{before ? before.name : "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-gray-500">Depois de você</div>
                    <div className="font-medium">{after ? after.name : "—"}</div>
                  </div>
                </div>
              )}
              {isNext && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-2 rounded-full bg-black px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-white">
                    <CheckCircle2 size={12} /> Você está na vez
                  </span>
                  {me.status === "available" && !activeCall && (
                    <button
                      data-testid={`btn-self-pass-${c.name.toLowerCase()}`}
                      onClick={() => openSelfPassDialog(c)}
                      className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-50"
                    >
                      <SkipForward size={12} /> Passar minha vez
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Reason picker dialog for Pausar */}
      <PauseReasonDialog
        open={pauseDialogOpen}
        onOpenChange={setPauseDialogOpen}
        onConfirm={submitPause}
        title="Pausar — qual o motivo?"
        description="Escolha o motivo. Se for Almoço, você sai das filas até voltar."
        testidPrefix="pause"
      />

      {/* Reason picker dialog */}
      <Dialog open={!!reasonDialog} onOpenChange={(o) => !o && setReasonDialog(null)}>
        <DialogContent data-testid="dialog-reason">
          <DialogHeader>
            <DialogTitle className="font-display">Passar a vez — qual o motivo?</DialogTitle>
            <DialogDescription>
              A recepção será avisada e você irá para o final da fila. Almoço também pausa você automaticamente.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2">
            {REASONS.map((r) => (
              <button
                key={r.key}
                data-testid={`btn-reason-${r.key}`}
                onClick={() => submitReason(r.key)}
                className="rounded-2xl border border-gray-200 bg-white p-4 text-left hover:border-black hover:bg-pink-50/50"
              >
                <div className="text-2xl">{r.emoji}</div>
                <div className="mt-1 font-medium">{r.label}</div>
                {r.auto_pause && (
                  <div className="mt-1 text-[10px] uppercase tracking-widest text-pink-600">Pausa automática</div>
                )}
              </button>
            ))}
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500">Observação (opcional)</label>
            <input
              data-testid="input-reason-note"
              value={freeNote}
              onChange={(e) => setFreeNote(e.target.value)}
              placeholder="Ex.: volto em 10 minutos"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-black"
            />
          </div>
          <DialogFooter>
            <button onClick={() => setReasonDialog(null)} className="rounded-full border border-gray-200 px-4 py-2 text-sm">
              Voltar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
